/**
 * HubCard — Algomed HomeHub modül kartlarının Telegram WebApp adaptasyonu.
 *
 * Kaynak desen: frontend/src/pages/HomeHubPage.jsx > MODULES + LinkBox blokları.
 * Mobil-only yatay layout (ikon kutusu solda, başlık+altyazı sağda) korunur.
 * Algomed `bgCard`/`borderPrimary`/`textPrimary`/`textSecondary` semantic
 * token'ları kullanılır; renk paleti ile ilgili tüm görünümü `tileBg`/`iconColor`
 * ile parametrik tutar.
 */
import { Box, Flex, HStack, VStack, Text, Badge, useColorModeValue } from '@chakra-ui/react';
import { haptic } from '../telegram';

export interface HubCardProps {
  title: string;
  subtitle: string;
  glyph: string;            // Algomed BRAND.emojiContent veya ICON setinden tek karakter
  /** Light mode tile background (semantic token veya color string). */
  lightTileBg?: string;
  /** Dark mode tile background (semantic token veya color string). */
  darkTileBg?: string;
  /** Tile içindeki glyph rengi (Chakra token: 'accent', 'flashcardAccent', vb.). */
  iconColor?: string;
  badgeText?: string;
  badgeScheme?: string;
  onClick: () => void;
}

export default function HubCard({
  title,
  subtitle,
  glyph,
  lightTileBg = 'blue.50',
  darkTileBg = 'whiteAlpha.100',
  iconColor = 'accent',
  badgeText,
  badgeScheme = 'blue',
  onClick,
}: HubCardProps) {
  const tileBg = useColorModeValue(lightTileBg, darkTileBg);
  const hoverOverlay = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');

  const handleClick = () => {
    haptic('select');
    onClick();
  };

  return (
    <Box
      as="button"
      type="button"
      onClick={handleClick}
      bg="bgCard"
      borderWidth="1px"
      borderColor="borderPrimary"
      borderRadius="xl"
      boxShadow="cardElevated"
      px={3}
      py={2.5}
      minH="92px"
      overflow="hidden"
      position="relative"
      role="group"
      textAlign="left"
      w="full"
      transition="transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease"
      _hover={{ transform: 'translateY(-2px)', borderColor: 'borderSecondary', boxShadow: 'lg' }}
      _active={{ transform: 'translateY(0)' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'accent', outlineOffset: '2px' }}
    >
      <Box
        position="absolute"
        inset={0}
        opacity={0}
        _groupHover={{ opacity: 1 }}
        transition="opacity 0.2s ease"
        bgGradient={`linear(to-b, transparent, ${hoverOverlay})`}
        pointerEvents="none"
      />

      {badgeText ? (
        <Badge
          position="absolute"
          top={2}
          right={2}
          zIndex={2}
          colorScheme={badgeScheme}
          borderRadius="full"
          textTransform="none"
          fontSize="10px"
          px={1.5}
          py={0.5}
          maxW="74px"
          noOfLines={1}
        >
          {badgeText}
        </Badge>
      ) : null}

      <HStack
        position="relative"
        zIndex={1}
        align="center"
        spacing={2.5}
        minW={0}
        h="full"
        pr={badgeText ? 12 : 0}
      >
        <Flex
          w="42px"
          h="42px"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="borderSecondary"
          bg={tileBg}
          align="center"
          justify="center"
          boxShadow="sm"
          flexShrink={0}
        >
          <Text fontSize="xl" lineHeight="1" color={iconColor} fontWeight="bold">
            {glyph}
          </Text>
        </Flex>

        <VStack align="start" justify="center" spacing={0.5} minW={0} flex="1">
          <Text
            color="textPrimary"
            fontWeight="bold"
            fontSize="sm"
            lineHeight="1.15"
            noOfLines={2}
            wordBreak="break-word"
          >
            {title}
          </Text>
          <Text
            color="textSecondary"
            fontSize="11px"
            lineHeight="1.25"
            noOfLines={2}
            wordBreak="break-word"
          >
            {subtitle}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}
